import React from 'react';

/**
 * VerificationResult component
 * Displays verification scores and blockchain transaction info
 */
function VerificationResult({ result }) {
  if (!result) return null;

  const { verification, explanation, assignment_hash, blockchain_tx } = result;

  /**
   * Get color class based on score
   */
  const getScoreColor = (score) => {
    if (score >= 80) return 'score-excellent';
    if (score >= 60) return 'score-good';
    if (score >= 40) return 'score-fair';
    return 'score-poor';
  };

  /**
   * Get score label
   */
  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Needs Improvement';
  };

  return (
    <div className="verification-result-section">
      <div className="result-card">
        <div className="result-header">
          <h2>✅ Verification Complete</h2>
        </div>

        {/* Overall Score */}
        <div className="overall-score">
          <div className={`score-circle ${getScoreColor(verification.overall_score)}`}>
            <span className="score-value">{verification.overall_score}</span>
            <span className="score-label">{getScoreLabel(verification.overall_score)}</span>
          </div>
        </div>

        {/* Detailed Scores */}
        <div className="score-breakdown">
          <h3>Score Breakdown</h3>
          
          <div className="score-item">
            <div className="score-info">
              <span className="score-name">Logical Consistency</span>
              <span className="score-percentage">{verification.logical_consistency}%</span>
            </div>
            <div className="progress-bar">
              <div 
                className={`progress-fill ${getScoreColor(verification.logical_consistency)}`}
                style={{ width: `${verification.logical_consistency}%` }}
              />
            </div>
          </div>

          <div className="score-item">
            <div className="score-info">
              <span className="score-name">Factual Accuracy</span>
              <span className="score-percentage">{verification.factual_accuracy}%</span>
            </div>
            <div className="progress-bar">
              <div 
                className={`progress-fill ${getScoreColor(verification.factual_accuracy)}`}
                style={{ width: `${verification.factual_accuracy}%` }}
              />
            </div>
          </div>

          <div className="score-item">
            <div className="score-info">
              <span className="score-name">Hallucination Risk</span>
              <span className="score-percentage">{verification.hallucination_risk}%</span>
            </div>
            <div className="progress-bar">
              <div 
                className={`progress-fill ${getScoreColor(100 - verification.hallucination_risk)}`}
                style={{ width: `${verification.hallucination_risk}%` }}
              />
            </div>
            <p className="score-note">Lower is better</p>
          </div>
        </div>

        {/* Explanation */}
        <div className="explanation-section">
          <h3>Analysis</h3>
          <p className="explanation-text">{explanation}</p>
        </div>

        {/* Blockchain Info */}
        <div className="blockchain-section">
          <h3>Blockchain Verification</h3>
          
          <div className="blockchain-info">
            <div className="info-item">
              <span className="info-label">Assignment Hash:</span>
              <code className="info-value hash-value">
                {assignment_hash}
              </code>
            </div>

            {blockchain_tx && (
              <div className="info-item">
                <span className="info-label">Transaction Hash:</span>
                <code className="info-value hash-value">
                  {blockchain_tx}
                </code>
                <a 
                  href={`https://monad-explorer.example.com/tx/${blockchain_tx}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="explorer-link"
                >
                  View on Explorer →
                </a>
              </div>
            )}
          </div>

          <p className="blockchain-note">
            🔒 This verification is permanently stored on the Monad blockchain and cannot be altered.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="result-actions">
          <button 
            className="btn btn-secondary"
            onClick={() => window.location.reload()}
          >
            Verify Another Assignment
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => {
              const resultText = `
Verification Result:
Overall Score: ${verification.overall_score}/100
Logical Consistency: ${verification.logical_consistency}%
Factual Accuracy: ${verification.factual_accuracy}%
Hallucination Risk: ${verification.hallucination_risk}%

Analysis: ${explanation}

Assignment Hash: ${assignment_hash}
Transaction: ${blockchain_tx || 'N/A'}
              `.trim();
              navigator.clipboard.writeText(resultText);
              alert('Result copied to clipboard!');
            }}
          >
            📋 Copy Result
          </button>
        </div>
      </div>
    </div>
  );
}

export default VerificationResult;
